import React from "react";
import { Skeleton } from "@material-ui/lab";
import { DataTableV2, Text, Pill } from "components/commons";
import locale from "localization";
import styles from "./purchase-order-form/purchase-order.module.scss";
import { prettifyDeliveryRecipient } from "utils/pretty.utils";
import { dateTimeFormat } from "utils";
import useDataTable from "hooks/useDataTable";
import { fuelColumns, nonFuelColumns } from "./purchase-order-form/products/columns";
import { mapDataToListViewFuel } from "./purchase-order-form/products/mapper";
import useProducts from "hooks/useProducts";
import { useMount } from "hooks";
import { OrderStatus } from "enums";
import { prettifyOrderStatus } from "utils/pretty.utils";
import { capitalizeWord } from "utils/text.utils";
import WarningAmberIcon from "@material-ui/icons/WarningTwoTone";
import ModeEditIcon from "@material-ui/icons/EditOutlined";

const DisplayText = ({ value, formatter, width = "300px", loading }) => {
  if (loading) return <Skeleton width={width} height="30px" />;

  if (React.isValidElement(value)) {
    return value;
  }

  return <>{formatter ? formatter(value) : value}</>;
};

const ViewRow = ({ label, value, formatter, loading }) => (
  <div className={styles.viewRow}>
    <Text strong color="black">
      {label}
    </Text>
    <Text color="black">
      <DisplayText value={value} formatter={formatter} loading={loading} />
    </Text>
  </div>
);

const ViewPurchaseOrder = ({ purchaseOrder, loading }) => {
  const { order } = purchaseOrder || {};
  const { allProducts, fetch: getAllProducts } = useProducts({ withStationCode: true });

  useMount(async () => {
    await getAllProducts();
  });

  const isFuelOnly = order?.productType === "fuel";
  const { table } = useDataTable({
    value: order?.orderItems || [],
    filter: { initialState: {}, mapper: (param) => param },
    table: {
      key: "orders",
      mapper: (orders) => {
        if (isFuelOnly) {
          return mapDataToListViewFuel({
            orders,
            allProducts,
          });
        }
      },
      columns: isFuelOnly
        ? fuelColumns.filter((c) => c.key !== "actions")
        : nonFuelColumns.filter((c) => c.key !== "actions"),
    },
  });

  return (
    <>
      <div className={styles.viewForm}>
        <Text strong color="black" className={styles.sectionTitle}>
          {locale.orderDetails}
        </Text>

        <ViewRow
          label={locale.status}
          value={
            <Pill
              className={styles.status}
              cheddar={order?.status === OrderStatus.ForRevision}
              grass={order?.status === OrderStatus.Delivered}
              cement={order?.status === OrderStatus.Canceled}
              deepRed={order?.status === OrderStatus.Rejected}
              sky={order?.status === OrderStatus.Processing}
              purple={order?.status === OrderStatus.ForDelivery}
              mainlyBlue={order?.status === OrderStatus.ForApproval}
            >
              {prettifyOrderStatus(order?.status)}
            </Pill>
          }
          loading={loading}
        />

        <ViewRow
          label={locale.purchaseOrderNo}
          value={order?.purchaseOrderNumber ?? "-"}
          loading={loading}
        />

        <ViewRow
          label={locale.salesOrderNo}
          value={order?.salesOrderNumber ?? "-"}
          loading={loading}
        />

        <ViewRow
          label={locale.orderRecipient}
          value={prettifyDeliveryRecipient(order?.recipient)}
          loading={loading}
        />

        <ViewRow
          label={locale.preferredDeliveryDateTime}
          value={dateTimeFormat(order?.preferredDeliveryDate) ?? ""}
          loading={loading}
        />

        <ViewRow
          label={locale.productType}
          value={capitalizeWord(order?.productType) ?? "-"}
          loading={loading}
        />

        <ViewRow label={locale.remarks} value={order?.remarks} loading={loading} />
        <br />
        <br />
        <ViewRow label={locale.headOfficeRemarks} value={order?.remarksHo} loading={loading} />
        <br />
        <br />
        <Text strong color="black" className={styles.sectionTitle}>
          {locale.itemOrder}
        </Text>
      </div>
      <DataTableV2
        {...table}
        size="small"
        hidePagination
        hideNoSearchResultRender
        tableVersion="v2"
        striped={true}
        className={styles.tableContainerView}
      />
      {order?.reason &&
        (order?.status === OrderStatus.Rejected || order?.status === OrderStatus.ForRevision) && (
          <>
            <div className={styles.viewRemarks}>
              <div
                className={
                  order?.status === OrderStatus.Rejected
                    ? styles.remarksForRejected
                    : styles.remarksForRevision
                }
              >
                <div className={styles.rejectHeader}>
                  {order?.status === OrderStatus.Rejected ? (
                    <>
                      <WarningAmberIcon className={styles.warningIcon} />
                      <div className={styles.content}>
                        <Text strong color="red" style={{ fontWeight: "bold" }}>
                          {locale.rejectReason}
                        </Text>
                        <Text label>{`(${dateTimeFormat(order?.updatedAt)})`}</Text>
                      </div>
                    </>
                  ) : (
                    <>
                      <ModeEditIcon className={styles.editIcon} />
                      <div className={styles.content}>
                        <Text warning color="#f2994b" style={{ fontWeight: "bold" }}>
                          {locale.revisionRequestReason}
                        </Text>
                        <Text label>{`(${dateTimeFormat(order?.updatedAt)})`}</Text>
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.rejectDetails}>
                  <Text color="black">{order?.reason}</Text>
                </div>

                <div className={styles.rejectRemarks}>
                  <Text strong color="black">
                    {locale.remarks}
                  </Text>
                  <Text color="black">{order?.reasonRemarks || "-"}</Text>
                </div>
              </div>
            </div>
          </>
        )}
    </>
  );
};

export default ViewPurchaseOrder;
